/**
 * SafetyNumberCard — pure card rendering a 60-digit encryption safety
 * number as a 12x5 digit grid (no crypto, no store, no i18n; the host
 * computes `digits` via `computeSafetyNumber` from `@reflecto/core` and
 * wires everything else in). See
 * docs/superpowers/specs/2026-07-13-safety-numbers-design.md §5.
 *
 * The fingerprint is derived from public keys only — it is public data,
 * safe to display/copy/screenshot. `masked` mirrors the generic
 * `publicMode` shoulder-surfing guard used elsewhere in the package
 * (notification content, ClipCard); it is NOT a secrecy boundary for
 * this component, so — unlike ClipCard — there is no reveal-on-tap.
 * `onCopy` performs the OS-clipboard write (platform seam stays outside
 * client-ui) and is handed the SAME grouped string the grid renders, so
 * what the user copies can never drift from what they compare against.
 * It is awaited like ClipCard's: a rejected write flashes nothing.
 */

import { Check, Copy01 } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "../IconButton";
import { Text } from "../Text";
import styles from "./SafetyNumberCard.module.css";

const GROUP_SIZE = 5;
const MASK_GROUP = "•".repeat(GROUP_SIZE);

/** Splits the ungrouped digit string into 5-digit groups for display (12 groups for the canonical 60-digit fingerprint). */
function groupDigits(digits: string): string[] {
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += GROUP_SIZE) {
    groups.push(digits.slice(i, i + GROUP_SIZE));
  }
  return groups;
}

export interface SafetyNumberCardLabels {
  /** Card heading, e.g. "Verify encryption". */
  title: string;
  /** Caption naming the paired device this number verifies — called with `deviceLabel`. */
  caption: (deviceLabel: string) => string;
  /** aria-label for the copy button. Rendered only when `onCopy` is supplied. */
  copy: string;
  /** aria-label for the copy button immediately after a copy (brief confirmation). */
  copied: string;
  /** sr-only text announced while the grid is masked. */
  maskedHint: string;
}

export interface SafetyNumberCardProps {
  /** The 60-digit ungrouped fingerprint; the card does the 12x5 grouping for display. */
  digits: string;
  /** Display name of the paired device this number verifies. */
  deviceLabel: string;
  /** Privacy ("public") mode — masks the digit grid, same spirit as notification-content masking elsewhere in the package. */
  masked: boolean;
  /** Write the grouped fingerprint ("12345 67890 …") to the OS clipboard. Copy affordance renders only when supplied. */
  onCopy?: (grouped: string) => Promise<void>;
  labels: SafetyNumberCardLabels;
}

export function SafetyNumberCard({
  digits,
  deviceLabel,
  masked,
  onCopy,
  labels,
}: SafetyNumberCardProps) {
  const [copied, setCopied] = useState(false);
  // Holds the "copied" flash timer so we can cancel it if the card
  // unmounts within 1.5s of a copy, avoiding a setState on an
  // unmounted component (same guard as ClipCard).
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(copiedTimer.current);
    };
  }, []);

  const groups = groupDigits(digits);

  const handleCopy = useCallback(() => {
    if (!onCopy) return;
    // Same shape as ClipCard: the write happens inside the click handler
    // so it carries user activation, and the "copied" flash fires only
    // once the write actually resolves. In the MV3 side panel writeText
    // rejects whenever the panel document lacks OS focus, so flashing
    // unconditionally would tell the user a lie right before they walk
    // to their phone to compare digits.
    void onCopy(groups.join(" ")).then(
      () => {
        if (!mountedRef.current) return;
        setCopied(true);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => {
          if (mountedRef.current) setCopied(false);
        }, 1500);
      },
      () => {
        // Swallow — a failed copy simply doesn't flash. The digits are
        // still on screen, so the user can always compare by eye.
      }
    );
  }, [groups, onCopy]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Text as="h3" size="s" weight="semibold" tone="primary">
          {labels.title}
        </Text>
        {onCopy && (
          <IconButton
            icon={copied ? Check : Copy01}
            label={copied ? labels.copied : labels.copy}
            onClick={handleCopy}
          />
        )}
      </div>

      <Text size="xs" tone="secondary">
        {labels.caption(deviceLabel)}
      </Text>

      {/* aria-hidden only while masked (bullets carry no information),
          mirroring ClipCard's `.content[data-masked]` pattern — when
          unmasked the digits are public data and stay readable by AT. */}
      <div className={styles.grid} aria-hidden={masked || undefined}>
        {groups.map((group, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are a fixed-length, non-reorderable derivation of `digits` (matches Notification's `action.id ?? index` precedent).
          <span key={i} className={styles.group} data-masked={masked || undefined}>
            {masked ? MASK_GROUP : group}
          </span>
        ))}
      </div>

      {masked && <span className={styles.srOnly}>{labels.maskedHint}</span>}
    </div>
  );
}
